import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { brandTrustedDomains } from '@rock/brands';
import type { Brand } from '@rock/types';
import { Plus, Trash2 } from 'lucide-react';
import type { FC } from 'react';
import { useMemo, useState } from 'react';
import { BRAND_DOMAIN_SOURCE_LABEL, buildBrandDomainRows, isRemovableDomainSource } from './brandDomainRows';

interface BrandDomainsTabProps {
    brand: Brand;
    /** A merged brand's domains moved to its winner — show them, change nothing. */
    readOnly?: boolean;
    onAddDomain: (domainInput: string) => Promise<void>;
    onRemoveDomain: (domain: string) => Promise<void>;
    onAddTrustedDomain: (domainInput: string) => Promise<void>;
    onRemoveTrustedDomain: (domain: string) => Promise<void>;
}

/** The hostnames the coming auto-confirm gate credits for this brand's documents: owned, then trusted. */
export const BrandDomainsTab: FC<BrandDomainsTabProps> = ({
    brand,
    readOnly,
    onAddDomain,
    onRemoveDomain,
    onAddTrustedDomain,
    onRemoveTrustedDomain,
}) => (
    <div className="flex flex-col gap-4">
        <OwnedDomainsCard brand={brand} readOnly={readOnly} onAdd={onAddDomain} onRemove={onRemoveDomain} />
        <TrustedDomainsCard
            brand={brand}
            readOnly={readOnly}
            onAdd={onAddTrustedDomain}
            onRemove={onRemoveTrustedDomain}
        />
    </div>
);

interface DomainsCardProps {
    brand: Brand;
    readOnly?: boolean;
    onAdd: (domainInput: string) => Promise<void>;
    onRemove: (domain: string) => Promise<void>;
}

const OwnedDomainsCard: FC<DomainsCardProps> = ({ brand, readOnly, onAdd, onRemove }) => {
    const rows = useMemo(() => buildBrandDomainRows(brand), [brand]);
    const [isMutating, track] = usePending();

    return (
        <Card>
            <CardHeader>
                <CardTitle>Owned domains</CardTitle>
                <CardDescription>
                    Every hostname this brand serves content from — regional sites, support portals, and the asset/CDN
                    hosts its manual PDFs download from. The coming auto-confirm gate will count documents fetched from
                    these hosts (or any subdomain) as manufacturer-published. A separate assets domain must be listed
                    explicitly; subdomains are covered automatically.
                </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
                <AddDomainInput
                    placeholder="Add a domain (e.g. whirlpoolassets.com or a pasted URL)"
                    disabled={readOnly || isMutating}
                    onAdd={track(onAdd)}
                />
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Domain</TableHead>
                            <TableHead className="w-[140px]">Source</TableHead>
                            <TableHead className="w-[80px] text-right" aria-label="Actions" />
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {rows.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={3} className="text-sm text-muted-foreground">
                                    No domains yet — set the website on Details, run Brandfetch, or add one above.
                                </TableCell>
                            </TableRow>
                        ) : (
                            rows.map((row) => (
                                <TableRow key={row.domain}>
                                    <DomainCell domain={row.domain} />
                                    <TableCell>
                                        {row.source === 'explicit' ? (
                                            BRAND_DOMAIN_SOURCE_LABEL[row.source]
                                        ) : (
                                            <Badge variant="secondary">{BRAND_DOMAIN_SOURCE_LABEL[row.source]}</Badge>
                                        )}
                                    </TableCell>
                                    <TableCell className="text-right">
                                        {isRemovableDomainSource(row.source) && !readOnly && (
                                            <RemoveDomainButton
                                                domain={row.domain}
                                                disabled={isMutating}
                                                onRemove={track(onRemove)}
                                            />
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </CardContent>
        </Card>
    );
};

const TrustedDomainsCard: FC<DomainsCardProps> = ({ brand, readOnly, onAdd, onRemove }) => {
    const domains = useMemo(() => brandTrustedDomains(brand), [brand]);
    const [isMutating, track] = usePending();

    return (
        <Card>
            <CardHeader>
                <CardTitle>Trusted domains</CardTitle>
                <CardDescription>
                    Hosts this brand doesn't own whose copies of its manuals you vouch for, like an authorized dealer.
                    The coming auto-confirm gate counts this brand's documents served from (or linked from) these hosts.
                    Only this brand's documents: another brand's manual on the same host earns nothing.
                </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
                <AddDomainInput
                    placeholder="Add a trusted domain (e.g. a dealer's site)"
                    disabled={readOnly || isMutating}
                    onAdd={track(onAdd)}
                />
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Domain</TableHead>
                            <TableHead className="w-[80px] text-right" aria-label="Actions" />
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {domains.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={2} className="text-sm text-muted-foreground">
                                    No trusted domains.
                                </TableCell>
                            </TableRow>
                        ) : (
                            domains.map((domain) => (
                                <TableRow key={domain}>
                                    <DomainCell domain={domain} />
                                    <TableCell className="text-right">
                                        {!readOnly && (
                                            <RemoveDomainButton
                                                domain={domain}
                                                disabled={isMutating}
                                                onRemove={track(onRemove)}
                                            />
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </CardContent>
        </Card>
    );
};

/** One in-flight flag for a card's add and remove: both write a whole list computed from the brand on screen. */
function usePending(): [boolean, (write: (domain: string) => Promise<void>) => (domain: string) => Promise<void>] {
    const [isPending, setIsPending] = useState(false);
    const track = (write: (domain: string) => Promise<void>) => async (domain: string) => {
        setIsPending(true);
        try {
            await write(domain);
        } finally {
            setIsPending(false);
        }
    };
    return [isPending, track];
}

const AddDomainInput: FC<{
    placeholder: string;
    disabled?: boolean;
    onAdd: (domainInput: string) => Promise<void>;
}> = ({ placeholder, disabled, onAdd }) => {
    const [domainInput, setDomainInput] = useState('');
    const canAdd = !!domainInput.trim() && !disabled;

    const handleAdd = async () => {
        if (!canAdd) return;
        try {
            await onAdd(domainInput);
            setDomainInput('');
        } catch {
            // Toast handled by the page; keep the input so the admin can adjust.
        }
    };

    return (
        <div className="flex gap-2">
            <Input
                placeholder={placeholder}
                value={domainInput}
                onChange={(e) => setDomainInput(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' && canAdd) {
                        void handleAdd();
                    }
                }}
                disabled={disabled}
            />
            <Button onClick={handleAdd} disabled={!canAdd}>
                <Plus className="mr-2 h-4 w-4" />
                Add
            </Button>
        </div>
    );
};

const DomainCell: FC<{ domain: string }> = ({ domain }) => (
    <TableCell className="font-mono font-medium">
        <a
            href={`https://${domain}`}
            target="_blank"
            rel="noreferrer"
            className="text-primary underline-offset-2 hover:underline"
        >
            {domain}
        </a>
    </TableCell>
);

const RemoveDomainButton: FC<{ domain: string; disabled: boolean; onRemove: (domain: string) => Promise<void> }> = ({
    domain,
    disabled,
    onRemove,
}) => (
    <Button
        variant="ghost"
        size="icon"
        onClick={() => void onRemove(domain)}
        disabled={disabled}
        aria-label={`Remove domain ${domain}`}
    >
        <Trash2 className="h-4 w-4 text-destructive" />
    </Button>
);
